import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import config from '../../config/env';
import {
  ensureNotificationPermission,
  showChatOsNotification,
} from '../../utils/chatNotifications';

const DISMISS_KEY = 'tg_chat_notify_dismissed_v1';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    if (Date.now() > until) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Used by TesterShellLayout for single-banner priority. */
export function isChatNotifyBannerEligible() {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission !== 'default') return false;
  return !isDismissed();
}

function persistDismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
  } catch {
    // ignore
  }
}

/**
 * For authenticated testers: request notification permission and show OS alerts
 * when a support chat message arrives (even if the Messages page is not open).
 *
 * @param {{ forceShow?: boolean, onDismiss?: () => void }} props
 */
export default function TesterChatNotifications({ forceShow = true, onDismiss }) {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const [showPermissionBanner, setShowPermissionBanner] = useState(false);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (!user || user.role !== 'tester') return undefined;
    if (!('Notification' in window)) return undefined;

    if (Notification.permission === 'default' && !isDismissed()) {
      setShowPermissionBanner(true);
    } else {
      setShowPermissionBanner(false);
    }

    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let socket;
    try {
      socket = io(config.socketUrl, {
        transports: ['websocket', 'polling'],
        auth: { token },
      });
    } catch {
      return undefined;
    }

    const onDm = (payload) => {
      const msg = payload?.message || payload;
      if (!msg) return;
      const fromId = msg.fromUserId || msg.sender?.id;
      if (!fromId || fromId === user.id) return;

      const sender =
        [msg.sender?.firstName, msg.sender?.lastName].filter(Boolean).join(' ') ||
        'Support';
      const text =
        msg.message && msg.message !== '(attachment)'
          ? msg.message
          : Array.isArray(msg.attachments) && msg.attachments.length
            ? 'Sent an attachment'
            : 'New message';

      const onMessages = pathRef.current.startsWith('/app/messages');
      if (onMessages && !document.hidden) return;

      showChatOsNotification({
        title: sender,
        body: String(text).slice(0, 140),
        tag: `dm-${fromId}`,
        url: '/app/messages',
      });
    };

    socket.on('direct-message', onDm);

    return () => {
      socket.off('direct-message', onDm);
      socket.disconnect();
    };
  }, [user]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  if (!user || user.role !== 'tester' || !showPermissionBanner || !forceShow) return null;

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: '#faf0f1',
        border: '1px solid #bfdbfe',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        flexWrap: 'wrap',
      }}
    >
      <NotificationsActiveIcon sx={{ color: '#cc0000', mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#a80000', mb: 0.35 }}>
          Enable chat notifications
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#1e3a8a', lineHeight: 1.45 }}>
          Get an alert when support messages you — even if TestGate is in the background.
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        onClick={async () => {
          const result = await ensureNotificationPermission();
          setShowPermissionBanner(result !== 'granted');
        }}
        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#cc0000' }}
      >
        Allow
      </Button>
      <Button
        size="small"
        onClick={() => {
          persistDismiss();
          setShowPermissionBanner(false);
          onDismiss?.();
        }}
        sx={{ textTransform: 'none', color: '#64748b' }}
      >
        Not now
      </Button>
    </Box>
  );
}
